import { useState, useEffect } from "react";
import { API, useTemplates } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Upload, FileText, Edit2, Check, X, Loader2, Image, ShieldAlert, Star } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export default function SettingsPage() {
  const { refresh: refreshTemplateContext } = useTemplates();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", system_prompt: "", multi_slide_prompt: "", single_slide_content_prompt: "" });
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/design-templates`);
      setTemplates(res.data);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error("Name is required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/design-templates`, createForm);
      toast.success("Template created");
      setShowCreate(false);
      setCreateForm({ name: "", system_prompt: "", multi_slide_prompt: "", single_slide_content_prompt: "" });
      fetchTemplates();
      refreshTemplateContext();
    } catch { toast.error("Failed to create"); }
    finally { setCreating(false); }
  };

  const handleSave = async (id) => {
    try {
      await axios.put(`${API}/design-templates/${id}`, editForm);
      toast.success("Saved");
      setEditingId(null);
      fetchTemplates();
      refreshTemplateContext();
    } catch { toast.error("Failed to save"); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/design-templates/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      fetchTemplates();
      refreshTemplateContext();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); setDeleteId(null); }
  };

  const handleSetDefault = async (t) => {
    if (t.is_default) return;
    if (!window.confirm(`Make "${t.name}" the default template?\n\nNew users + decks without an explicit template will land on this template instead of the current default.`)) {
      return;
    }
    try {
      await axios.post(`${API}/design-templates/${t.id}/set-default`);
      toast.success(`"${t.name}" is now the default template`);
      fetchTemplates();
      refreshTemplateContext();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set default");
    }
  };

  const handleUploadRef = async (templateId, files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await axios.post(`${API}/design-templates/${templateId}/design-references`, fd);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    toast.success("Uploaded");
    fetchTemplates();
  };

  const handleDeleteRef = async (templateId, refId) => {
    try {
      await axios.delete(`${API}/design-templates/${templateId}/design-references/${refId}`);
      fetchTemplates();
    } catch { toast.error("Failed to delete reference"); }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditForm({ name: t.name, system_prompt: t.system_prompt, multi_slide_prompt: t.multi_slide_prompt, single_slide_content_prompt: t.single_slide_content_prompt || "" });
  };

  if (loading) return <div className="page-container flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="page-container" data-testid="settings-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Home</span><span>/</span><span className="text-gray-900">Settings</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold font-['Manrope']">Design Templates</h1>
        <Button onClick={() => setShowCreate(true)} className="bg-black hover:bg-gray-800" data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-1.5" />New Template
        </Button>
      </div>

      <TrialTemplateConfig templates={templates} />

      <div className="space-y-6">
        {templates.map(t => (
          <Card key={t.id} className="border-gray-200 shadow-none" data-testid={`template-${t.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                {editingId === t.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))}
                      className="text-lg font-semibold max-w-xs" data-testid="edit-template-name" />
                    <Button size="sm" variant="ghost" onClick={() => handleSave(t.id)} data-testid="save-template-btn"><Check className="w-4 h-4 text-green-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4 text-gray-400" /></Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{t.name}</CardTitle>
                    {t.is_default && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">DEFAULT</span>}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  {!t.is_default && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSetDefault(t)}
                      className="text-gray-400 hover:text-amber-500"
                      title="Set as default template"
                      data-testid={`set-default-${t.id}`}
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => startEdit(t)} data-testid={`edit-${t.id}`}><Edit2 className="w-4 h-4" /></Button>
                  {!t.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(t.id)} className="text-gray-400 hover:text-red-500"
                      data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingId === t.id ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">System Prompt (Single Slide)</label>
                    <Textarea value={editForm.system_prompt} onChange={e => setEditForm(p => ({...p, system_prompt: e.target.value}))}
                      className="min-h-[100px] text-sm font-mono" data-testid="edit-system-prompt" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Single Slide Content Prompt</label>
                    <Textarea value={editForm.single_slide_content_prompt} onChange={e => setEditForm(p => ({...p, single_slide_content_prompt: e.target.value}))}
                      className="min-h-[100px] text-sm font-mono" data-testid="edit-single-slide-content-prompt" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Multi-slide Prompt (Storyline)</label>
                    <Textarea value={editForm.multi_slide_prompt} onChange={e => setEditForm(p => ({...p, multi_slide_prompt: e.target.value}))}
                      className="min-h-[100px] text-sm font-mono" data-testid="edit-multi-prompt" />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">System Prompt</label>
                    <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3 max-h-24 overflow-y-auto font-mono leading-relaxed">
                      {t.system_prompt?.slice(0, 300)}{t.system_prompt?.length > 300 && "..."}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Slide Content Prompt</label>
                    <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3 max-h-24 overflow-y-auto font-mono leading-relaxed">
                      {t.single_slide_content_prompt?.slice(0, 300)}{t.single_slide_content_prompt?.length > 300 && "..."}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Multi-slide Prompt</label>
                    <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3 max-h-24 overflow-y-auto font-mono leading-relaxed">
                      {t.multi_slide_prompt?.slice(0, 300)}{t.multi_slide_prompt?.length > 300 && "..."}
                    </div>
                  </div>
                </div>
              )}

              {/* Design References */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">
                  Design Reference Samples ({t.design_references?.length || 0})
                </label>
                <div className="flex flex-wrap gap-2">
                  {(t.design_references || []).map(ref => (
                    <div key={ref.id} className="relative group w-20 h-20 rounded-md overflow-hidden border border-gray-200 bg-gray-50">
                      {ref.thumbnail_url ? (
                        <img src={ref.thumbnail_url} alt={ref.filename} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Image className="w-5 h-5 text-gray-300" /></div>
                      )}
                      <button onClick={() => handleDeleteRef(t.id, ref.id)}
                        className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 rounded-md border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                    data-testid={`upload-ref-${t.id}`}>
                    <Plus className="w-5 h-5 text-gray-300" />
                    <input type="file" multiple accept="image/*" className="hidden"
                      onChange={e => e.target.files?.length && handleUploadRef(t.id, Array.from(e.target.files))} />
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Design Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Template name" value={createForm.name} onChange={e => setCreateForm(p => ({...p, name: e.target.value}))} data-testid="new-template-name" />
            <Textarea placeholder="System prompt for single-slide generation..." value={createForm.system_prompt}
              onChange={e => setCreateForm(p => ({...p, system_prompt: e.target.value}))} className="min-h-[80px] text-sm" data-testid="new-system-prompt" />
            <Textarea placeholder="Single slide content prompt (Raw Data → Slide Content)..." value={createForm.single_slide_content_prompt}
              onChange={e => setCreateForm(p => ({...p, single_slide_content_prompt: e.target.value}))} className="min-h-[80px] text-sm" data-testid="new-single-slide-content-prompt" />
            <Textarea placeholder="Multi-slide prompt for storyline generation..." value={createForm.multi_slide_prompt}
              onChange={e => setCreateForm(p => ({...p, multi_slide_prompt: e.target.value}))} className="min-h-[80px] text-sm" data-testid="new-multi-prompt" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-black hover:bg-gray-800">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete template?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500">This will permanently delete the template and its design references.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// TrialTemplateConfig — admin picks which templates new trial
// users get access to by default. Stored under settings key
// `trial_template_ids`. Falls back to "Wokelo Default only" when
// unset (handled server-side).
// ─────────────────────────────────────────────────────────────
function TrialTemplateConfig({ templates }) {
  const [selected, setSelected] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/trial-templates`, { withCredentials: true });
        const ids = r.data?.template_ids || [];
        setSelected(ids);
        setInitial(ids);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...initial].sort());

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one template — trial users need something to start with.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/trial-templates`, { template_ids: selected }, { withCredentials: true });
      setInitial(selected);
      toast.success("Trial template defaults updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/40 shadow-none mb-6" data-testid="trial-templates-config">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-amber-900 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" />
          Trial-user template access
        </CardTitle>
        <p className="text-xs text-amber-800/80 mt-1">
          New trial accounts will only see the templates checked here. Existing trial users keep whatever they currently have access to (override per-user from User Management).
        </p>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-2">
          {templates.map(t => (
            <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`trial-tpl-${t.id}`}>
              <Checkbox
                checked={selected.includes(t.id)}
                onCheckedChange={() => toggle(t.id)}
              />
              <span className="text-gray-800">{t.name}{t.is_default ? " (Default)" : ""}</span>
            </label>
          ))}
        </div>
        {dirty && (
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="save-trial-templates">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save changes"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(initial)} disabled={saving}>Cancel</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

